import type React from 'react';
import _ from 'lodash';
import { type CBNavItem } from '@chargebee/cb-navbar';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { type ActiveCompany } from '@bbkAdminRedux/app/reducers';
import type LinkGroupMenu from '@bbkAdminComponents/linkGroup/link-group';
import type { BBKNavbarOpt } from '@bbkAdminComponents/app-layout/nav/bbk-navbar-opt';

type LinkGroupMenuItem = React.ComponentProps<
  typeof LinkGroupMenu
>['links'][number];
export class NavBarHelper {
  static filterNavbarOptions<T extends BBKNavbarOpt>(opts: T[]): T[] {
    return opts
      .filter((option) => option.show !== false)
      .map((option) => ({
        ...option,
        subItems: option.subItems
          ? this.filterNavbarOptions(option.subItems)
          : undefined,
      }));
  }

  static flattenNavbar(navbar: CBNavItem[]): CBNavItem[] {
    return navbar.flatMap((x) =>
      x.subItems ? this.flattenNavbar(x.subItems) : [x]
    );
  }

  static switchApp(companyKey: string, localAppKey: string): void {
    const pathname = window.location.pathname;
    let splitPath = pathname.split('/').slice(5);
    if (splitPath[0] === 'experiences' || splitPath[0] === 'settings') {
      splitPath = splitPath.slice(0, 1);
    }
    const currentPath = splitPath.join('/');
    const url = `/company/${companyKey}/app/${localAppKey}/${currentPath}`;
    BrightbackSession.setActiveApp({ encoded_id: localAppKey });
    BrightbackSession.setActiveCompany({ internal_name: companyKey });
    window.location.assign(url);
  }

  static buildAppsList(companies: ActiveCompany[]): LinkGroupMenuItem[] {
    return _(companies)
      .orderBy([(v) => v.name.toLowerCase()], ['asc'])
      .flatMap((c) => {
        const companyItem: LinkGroupMenuItem = {
          text: c.name,
          itemClasses: 'customerData',
          company: c,
        };
        const noAccessItem: LinkGroupMenuItem = {
          text: '(No Access)',
          classes: 'indent disabled',
          itemClasses: 'customerData',
          company: c,
          isApplication: true,
        };
        const appsItems: LinkGroupMenuItem[] = (c.applications ?? []).map(
          (a) => ({
            text: a.display_name,
            linkAction: () => this.switchApp(c.internal_name, a.encoded_id),
            classes: 'indent',
            itemClasses: 'customerData',
            company: c,
            app: a,
            isApplication: true,
          })
        );
        return [
          companyItem,
          ...(!c.applications?.length ? [noAccessItem] : appsItems),
        ];
      })
      .value();
  }
}
